import React, { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

import PageShell from '../components/PageShell'
import { useT } from '../i18n'
import type {
  ArtifactStatus,
  ArtifactVersionStatus,
  ArtifactVisibility,
  ReportSnapshotArtifactPreview
} from '../utils/reportArtifactStorage'
import {
  archiveCollaborationArtifact,
  loadCollaborationArtifactDetail,
  revokeCollaborationArtifact,
  type CollaborationArtifactDetail
} from '../utils/collaborationArtifacts'
import LotStoryCollaborationPreview from './lots/LotStoryCollaborationPreview'
import { getLotStoryReadinessLabel } from './lots/lotStoryLibraryUi'

const getStatusBadgeStyle = (status: ArtifactStatus): React.CSSProperties => {
  if (status === 'published') {
    return { backgroundColor: '#dcfce7', color: '#166534' }
  }

  if (status === 'draft') {
    return { backgroundColor: '#dbeafe', color: '#1d4ed8' }
  }

  if (status === 'archived') {
    return { backgroundColor: '#f3f4f6', color: '#374151' }
  }

  return { backgroundColor: '#fee2e2', color: '#991b1b' }
}

// Same treatment as Reports' results/draft-preview tables — plain <table>
// markup otherwise falls back to raw browser defaults.
const tableWrapperStyle: React.CSSProperties = {
  overflow: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: 12
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse'
}

const tableHeaderCellStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: '#475569',
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap'
}

const tableCellStyle = (isLastRow: boolean): React.CSSProperties => ({
  padding: '10px 12px',
  verticalAlign: 'top',
  borderBottom: isLastRow ? 'none' : '1px solid #f1f5f9'
})

const tableRowStyle = (rowIndex: number): React.CSSProperties => ({
  backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc'
})

const formatDateTime = (value: string | null): string => {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleString()
}

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    const json = JSON.stringify(value)
    return typeof json === 'string' ? json : ''
  } catch {
    return ''
  }
}

const getStatusLabel = (status: ArtifactStatus, t: ReturnType<typeof useT>): string => {
  switch (status) {
    case 'draft':
      return t('collaborationLibrary.status.draft')
    case 'published':
      return t('collaborationLibrary.status.published')
    case 'archived':
      return t('collaborationLibrary.status.archived')
    case 'revoked':
      return t('collaborationLibrary.status.revoked')
    default:
      return status
  }
}

const getVisibilityLabel = (visibility: ArtifactVisibility, t: ReturnType<typeof useT>): string => {
  switch (visibility) {
    case 'private':
      return t('collaborationLibrary.visibility.private')
    case 'group':
      return t('collaborationLibrary.visibility.group')
    case 'agora':
      return t('collaborationLibrary.visibility.agora')
    default:
      return visibility
  }
}

const getVersionStatusLabel = (status: ArtifactVersionStatus, t: ReturnType<typeof useT>): string => {
  switch (status) {
    case 'published':
      return t('collaborationDetail.versionStatus.published')
    case 'superseded':
      return t('collaborationDetail.versionStatus.superseded')
    case 'revoked':
      return t('collaborationDetail.versionStatus.revoked')
    default:
      return status
  }
}

const getArtifactTypeLabel = (
  artifactType: CollaborationArtifactDetail['artifactType'],
  t: ReturnType<typeof useT>
): string => {
  if (artifactType === 'lot-story') {
    return t('collaborationLibrary.type.lotStory')
  }

  return t('collaborationLibrary.type.reportSnapshot')
}

const getLifecycleEventLabel = (
  type: CollaborationArtifactDetail['provenance']['lifecycleEvents'][number]['type'],
  t: ReturnType<typeof useT>
): string => {
  switch (type) {
    case 'published':
      return t('collaborationDetail.versionStatus.published')
    case 'revoked':
      return t('collaborationDetail.versionStatus.revoked')
    case 'archived':
      return t('collaborationLibrary.status.archived')
    default:
      return type
  }
}

const isReportSnapshotDetail = (
  detail: CollaborationArtifactDetail
): detail is Extract<CollaborationArtifactDetail, { artifactType: 'report-snapshot' }> => (
  detail.artifactType === 'report-snapshot'
)

const buildScopeHighlights = (
  preview: ReportSnapshotArtifactPreview,
  t: ReturnType<typeof useT>
): string[] => {
  const scope = preview.scope
  if (!scope) return [t('collaborationDetail.preview.scopeNone')]

  const highlights: string[] = []

  if (scope.fromDate) {
    highlights.push(t('collaborationDetail.preview.scopeFrom', { date: scope.fromDate }))
  }

  if (scope.toDate) {
    highlights.push(t('collaborationDetail.preview.scopeTo', { date: scope.toDate }))
  }

  if (scope.farmIds.length > 0) {
    highlights.push(t('collaborationDetail.preview.scopeFacilities', { count: String(scope.farmIds.length) }))
  }

  if (scope.productionUnitIds.length > 0) {
    highlights.push(t('collaborationDetail.preview.scopeProductionUnits', { count: String(scope.productionUnitIds.length) }))
  }

  const lotCount = Math.max(scope.lotIds.length, scope.lotKeys.length)
  if (lotCount > 0) {
    highlights.push(t('collaborationDetail.preview.scopeLots', { count: String(lotCount) }))
  }

  return highlights.length > 0 ? highlights : [t('collaborationDetail.preview.scopeNone')]
}

export default function CommunityArtifactDetail() {
  const t = useT()
  const location = useLocation()
  const { artifactId = '' } = useParams<{ artifactId: string }>()
  const [detail, setDetail] = useState<CollaborationArtifactDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionStatus, setActionStatus] = useState<string | null>(null)
  const backToLibraryHref = location.search ? `/community${location.search}` : '/community'

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setActionError(null)
      setActionStatus(null)
      const nextDetail = artifactId ? await loadCollaborationArtifactDetail(artifactId) : null

      if (!cancelled) {
        setDetail(nextDetail)
        setIsLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [artifactId])

  if (isLoading) {
    return (
      <PageShell>
        <Link to={backToLibraryHref} style={{ display: 'inline-block', marginBottom: 12 }}>
          {t('collaborationDetail.action.backToLibrary')}
        </Link>

        <div className="form-card">
          <h2 className="h2" style={{ marginTop: 0 }}>{t('collaborationDetail.loading')}</h2>
        </div>
      </PageShell>
    )
  }

  if (!detail) {
    return (
      <PageShell>
        <Link to={backToLibraryHref} style={{ display: 'inline-block', marginBottom: 12 }}>
          {t('collaborationDetail.action.backToLibrary')}
        </Link>

        <div className="form-card">
          <h2 className="h2" style={{ marginTop: 0 }}>{t('collaborationDetail.notFound.title')}</h2>
          <p className="lead">{t('collaborationDetail.notFound.subtitle')}</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
            <Link to={backToLibraryHref} className="btn-gradient" style={{ textDecoration: 'none' }}>
              {t('collaborationDetail.action.backToLibrary')}
            </Link>
            <Link to="/reports">{t('collaborationLibrary.empty.openReports')}</Link>
          </div>
        </div>
      </PageShell>
    )
  }

  const publishedAt = formatDateTime(detail.overview.publishedAt)
  const updatedAt = formatDateTime(detail.overview.updatedAt)
  const publishedBy = detail.provenance.publishedBy
  const scopeHighlights = isReportSnapshotDetail(detail)
    ? buildScopeHighlights(detail.snapshotPreview, t)
    : []

  const handleArchive = async () => {
    const nextDetail = await archiveCollaborationArtifact(detail)

    if (!nextDetail) {
      setActionError(t('collaborationDetail.actions.archiveError'))
      setActionStatus(null)
      return
    }

    setDetail(nextDetail)
    setActionError(null)
    setActionStatus(t('collaborationDetail.actions.archiveSuccess'))
  }

  const handleRevoke = async () => {
    const nextDetail = await revokeCollaborationArtifact(detail)

    if (!nextDetail) {
      setActionError(t('collaborationDetail.actions.revokeError'))
      setActionStatus(null)
      return
    }

    setDetail(nextDetail)
    setActionError(null)
    setActionStatus(t('collaborationDetail.actions.revokeSuccess'))
  }

  return (
    <PageShell>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          alignItems: 'start'
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 520px' }}>
          <Link to={backToLibraryHref} style={{ display: 'inline-block', marginBottom: 12 }}>
            {t('collaborationDetail.action.backToLibrary')}
          </Link>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span
              style={{
                padding: '4px 9px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                backgroundColor: '#eef2ff',
                color: '#3730a3'
              }}
            >
              {getArtifactTypeLabel(detail.artifactType, t)}
            </span>
            <span
              style={{
                padding: '4px 9px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                ...getStatusBadgeStyle(detail.overview.status)
              }}
            >
              {getStatusLabel(detail.overview.status, t)}
            </span>
          </div>

          <h2 className="h2" style={{ marginTop: 0, marginBottom: 6 }}>{detail.overview.title}</h2>
          <div className="lead">{detail.overview.contextLine}</div>
          <p style={{ marginTop: 12, marginBottom: 0, color: '#374151' }}>{detail.overview.summary}</p>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link
            to={detail.source.href}
            className="btn-gradient"
            style={{ textDecoration: 'none', alignSelf: 'start' }}
          >
            {t('collaborationLibrary.action.openSource')}
          </Link>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
        <div
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
          }}
        >
          <div className="form-card">
            <h3 className="h2" style={{ marginTop: 0 }}>{t('collaborationDetail.section.overview')}</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              <div><b>{t('collaborationLibrary.meta.version')}</b> {detail.overview.currentVersion}</div>
              <div><b>{t('collaborationLibrary.meta.visibility')}</b> {getVisibilityLabel(detail.overview.visibility, t)}</div>
              <div><b>{t('collaborationLibrary.meta.ownerTenant')}</b> {detail.overview.ownerTenantKey}</div>
              <div><b>{t('collaborationLibrary.meta.publishedAt')}</b> {publishedAt || t('collaborationLibrary.meta.notPublishedYet')}</div>
              <div><b>{t('collaborationLibrary.meta.updatedAt')}</b> {updatedAt || t('collaborationLibrary.meta.unknown')}</div>
            </div>
          </div>

          <div className="form-card">
            <h3 className="h2" style={{ marginTop: 0 }}>{t('collaborationDetail.section.provenance')}</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              <div><b>{t('collaborationDetail.provenance.source')}</b> {detail.source.label}</div>
              <div>
                <b>{t('collaborationDetail.provenance.sourceEntity')}</b>{' '}
                {detail.provenance.sourceRefs[0]?.entityId ?? t('collaborationDetail.provenance.notAvailable')}
              </div>
              <div>
                <b>{t('collaborationDetail.provenance.publishedBy')}</b>{' '}
                {publishedBy
                  ? `${publishedBy.displayName} (${publishedBy.tenantKey})`
                  : t('collaborationDetail.provenance.notAvailable')}
              </div>
              <div>
                <b>{t('collaborationDetail.provenance.lifecycle')}</b>
                <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                  {detail.provenance.lifecycleEvents.map((event) => (
                    <li key={`${event.type}-${event.version ?? 'none'}-${event.at}`}>
                      {(() => {
                        const versionSuffix = event.version ? ` v${event.version}` : ''
                        return `${getLifecycleEventLabel(event.type, t)}${versionSuffix} · ${formatDateTime(event.at)}`
                      })()}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="form-card">
          <h3 className="h2" style={{ marginTop: 0 }}>{t('collaborationDetail.section.preview')}</h3>

          {isReportSnapshotDetail(detail) ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: 16,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
                }}
              >
                <div style={{ display: 'grid', gap: 8 }}>
                  <div><b>{t('collaborationDetail.meta.dataset')}</b> {detail.snapshotPreview.datasetLabel}</div>
                  <div><b>{t('collaborationDetail.meta.generatedAt')}</b> {formatDateTime(detail.snapshotPreview.generatedAt) || t('collaborationLibrary.meta.unknown')}</div>
                  <div><b>{t('collaborationDetail.meta.rows')}</b> {detail.snapshotPreview.rowCount}</div>
                  <div><b>{t('collaborationDetail.meta.columns')}</b> {detail.snapshotPreview.columns.length}</div>
                </div>

                <div style={{ display: 'grid', gap: 8 }}>
                  <div><b>{t('collaborationDetail.preview.scopeTitle')}</b></div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {scopeHighlights.map((highlight) => (
                      <span
                        key={highlight}
                        style={{
                          padding: '4px 9px',
                          borderRadius: 999,
                          fontSize: 12,
                          backgroundColor: '#f3f4f6',
                          color: '#374151'
                        }}
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                {detail.snapshotPreview.columns.map((column) => (
                  <span
                    key={column.key}
                    style={{
                      padding: '4px 9px',
                      borderRadius: 999,
                      fontSize: 12,
                      backgroundColor: '#eef2ff',
                      color: '#3730a3'
                    }}
                  >
                    {column.label}
                  </span>
                ))}
              </div>

              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('collaborationDetail.preview.sampleTitle')}</div>
                {detail.snapshotPreview.sampleRows.length === 0 ? (
                  <p className="lead" style={{ marginBottom: 0 }}>{t('collaborationDetail.preview.sampleEmpty')}</p>
                ) : (
                  <div style={tableWrapperStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          {detail.snapshotPreview.columns.map((column) => (
                            <th key={column.key} style={tableHeaderCellStyle}>
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.snapshotPreview.sampleRows.map((row, rowIndex) => {
                          const rowId = row.id
                          const rowKey = typeof rowId === 'string' || typeof rowId === 'number'
                            ? String(rowId)
                            : `row-${rowIndex}`
                          const isLastRow = rowIndex === detail.snapshotPreview.sampleRows.length - 1

                          return (
                            <tr key={`${rowKey}-${rowIndex}`} style={tableRowStyle(rowIndex)}>
                              {detail.snapshotPreview.columns.map((column) => (
                                <td key={column.key} style={tableCellStyle(isLastRow)}>
                                  {formatCellValue(row[column.key])}
                                </td>
                              ))}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {detail.snapshotPreview.rowCount > detail.snapshotPreview.sampleRows.length && (
                  <p className="lead" style={{ marginTop: 8, marginBottom: 0 }}>
                    {t('collaborationDetail.preview.sampleTruncatedNote', {
                      shown: String(detail.snapshotPreview.sampleRows.length),
                      total: String(detail.snapshotPreview.rowCount)
                    })}{' '}
                    <Link to={detail.source.href}>{t('collaborationDetail.preview.sampleTruncatedLink')}</Link>
                  </p>
                )}
              </div>
            </>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                <div><b>{t('collaborationDetail.meta.generatedAt')}</b> {formatDateTime(detail.snapshotPreview.generatedAt) || t('collaborationLibrary.meta.unknown')}</div>
                <div><b>{t('lots.story.library.detail.readiness')}</b> {getLotStoryReadinessLabel(t, detail.snapshotPreview.readinessStatus)}</div>
              </div>

              {detail.snapshotPreview.notes ? (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('lots.story.draft.field.notes')}</div>
                  <div className="lead" style={{ whiteSpace: 'pre-wrap' }}>{detail.snapshotPreview.notes}</div>
                </div>
              ) : null}

              <LotStoryCollaborationPreview projection={detail.snapshotPreview.projection} t={t} />
            </div>
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
          }}
        >
          <div className="form-card">
            <h3 className="h2" style={{ marginTop: 0 }}>{t('collaborationDetail.section.versions')}</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
              {detail.versions.map((version) => (
                <li key={version.version}>
                  <div style={{ fontWeight: 700 }}>{`v${version.version}`}</div>
                  <div className="lead">{getVersionStatusLabel(version.status, t)}</div>
                  <div className="lead">
                    {version.publishedAt
                      ? `${t('collaborationDetail.versions.publishedAt')} ${formatDateTime(version.publishedAt)}`
                      : t('collaborationDetail.versions.notPublished')}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="form-card">
            <h3 className="h2" style={{ marginTop: 0 }}>{t('collaborationDetail.section.actions')}</h3>
            {detail.actions.canRevoke || detail.actions.canArchive ? (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {detail.actions.canRevoke ? (
                  <button type="button" onClick={() => void handleRevoke()} className="btn-gradient">
                    {t('collaborationDetail.actions.revoke')}
                  </button>
                ) : null}
                {detail.actions.canArchive ? (
                  <button type="button" onClick={() => void handleArchive()} className="btn-gradient">
                    {t('collaborationDetail.actions.archive')}
                  </button>
                ) : null}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                <p className="lead" style={{ marginBottom: 0 }}>{t('collaborationDetail.actions.none')}</p>
                {detail.artifactType === 'lot-story' ? (
                  <p className="lead" style={{ marginBottom: 0 }}>{t('collaborationDetail.actions.ownerManagedInSource')}</p>
                ) : null}
              </div>
            )}
            {actionStatus ? (
              <p
                className="lead"
                style={{
                  marginTop: 12,
                  marginBottom: 0,
                  color: '#166534'
                }}
              >
                {actionStatus}
              </p>
            ) : null}
            {actionError ? (
              <p
                className="lead"
                style={{
                  marginTop: 12,
                  marginBottom: 0,
                  color: '#991b1b'
                }}
              >
                {actionError}
              </p>
            ) : null}
            {detail.artifactType === 'report-snapshot' ? (
              <p className="lead" style={{ marginTop: 12, marginBottom: 0 }}>
                {t('collaborationDetail.actions.note')}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </PageShell>
  )
}
